import { useCallback, useEffect, useRef, useState } from 'react';
import { atom, useSetAtom } from 'jotai';
import { useFormContext } from 'react-hook-form';
import { t } from 'i18next';
import { RecordFilter, Recording } from '@/types/api';
import { useSetAudioFile, useUploadFile } from '@/hooks/apiHooks';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import {
  deleteFromQueue,
  getDaysUntilDeletion,
  getQueuedRecordById,
  AUDIO_QUEUE_CHANGED_EVENT,
  getQueuedRecords,
  uploadFailedDialogId,
} from '@/utils/audioUploadQueue';
import { queueRecordLock, runWithLock } from '@/utils/browserLock';
import { Record as QueuedRecord } from '@/utils/audioUploadDB';
import { uploadWithRetry } from '@/utils/audioUploadRetry';
import {
  getInvalidRecordingReason,
  parseHHMMSS,
} from '@/utils/recordingIntegrity';
import { captureRecordError } from '@/components/Record/util/sentry';

/** 문서 화면 「음성 파일」 칸의 업로드 실패 안내 — 복구 훅이 채우고 안내 줄이 읽는다 */
export type QueuedRecordNotice = {
  docId: string;
  count: number;
  /** 가장 먼저 자동 삭제되는 백업까지 남은 일수 */
  days: number;
  retrying: boolean;
  retry: () => void;
  dismiss: () => void;
};
export const queuedRecordNoticeAtom = atom<QueuedRecordNotice | null>(null);

/** 백업 1건 복구 결과 — recovered 만 「다시 올렸어요」 안내에 센다 */
type RecoverResult = 'recovered' | 'failed' | 'skipped' | 'discarded';

/**
 * 업로드하지 못한 녹음(백업 큐) 복구 — 문서에 미복구 녹음이 있으면 「음성 파일」 칸에 실패 안내를 띄운다.
 *
 * 문서당 백업이 여러 건일 수 있어(PORTAL-12582) 한 번에 모두 다시 올린다.
 * 실패한 건만 큐에 남고 다음 진입·재시도 때 다시 안내한다.
 *
 * 여러 탭에서 같은 백업을 동시에 올리지 않게 백업마다 탭 간 잠금을 잡고,
 * 잡은 뒤 백업이 아직 있는지 다시 본다 — 다른 탭이 먼저 올렸으면 건너뛴다(PORTAL-12586).
 * 건너뛴 건은 올린 것으로 세지 않는다 — 「다시 올렸어요」 안내는 이 탭이 올린 건수만 말한다.
 *
 * 복구는 업로드 → 문서 연결(useSetAudioFile) → 폼 반영 → 백업 삭제 순이다.
 * 예전엔 폼에만 붙이고 문서 연결 API 를 부르지 않아, 새로고침하면 녹음이 사라졌다
 * (recordings 는 회의록 수정 API 로 저장되지 않는다).
 */
const useQueuedRecordRecovery = (
  docId: string,
  setLoading: (loading: boolean) => void,
) => {
  const { getValues, setValue } = useFormContext<RecordFilter>();
  const dialog = useDialog();
  const { showSuccess } = useSnackbar();
  // 문서 화면이 다른 문서로 바뀐 뒤 끝나는 비동기 처리는 ref 로 지금 문서를 본다 —
  // 시작할 때의 docId 로 폼 · 안내를 갱신하면 새 문서에 이전 문서 것이 섞인다 (PORTAL-12584)
  const docIdRef = useRef(docId);
  docIdRef.current = docId;
  // 화면이 key 로 새로 그려지면(문서 이동) 이 훅은 내려가고 새 훅이 뜬다 — 폼(FormProvider)은 key 바깥이라
  // 그대로 이어서 쓰인다. 그래서 「지금 폼이 어느 문서를 싣고 있는지」는 폼의 docId(문서 로드 때 reset 으로 들어온다)로 본다.
  //  - 내려간 훅의 docIdRef(이전 문서)로 보면 새 문서 폼에 이전 녹음이 붙었고 (재QA 1차)
  //  - 「훅이 아직 떠 있는지」로 보면 다른 문서에 갔다가 같은 문서로 돌아온 경우까지 막혀, 서버엔 연결됐는데
  //    화면엔 녹음이 안 보였다 (재QA 2차, PORTAL-12584 · 12588)
  /** 지금 폼이 그 문서를 싣고 있는지 — 폼에 손대기 전에 확인한다 */
  const isShowingDoc = useCallback(
    (id: string) => getValues('docId') === id,
    [getValues],
  );

  /**
   * 업로드 → 문서 연결 → 폼 반영 → 백업 삭제. 성공하면 'recovered', 실패하면 백업은 그대로 'failed'.
   * 빈/손상 백업은 올려도 같은 결과라 지우고 'discarded' — 성공으로 세지 않는다 (PORTAL-12587)
   */
  const uploadAndAttach = useCallback(
    async (record: QueuedRecord): Promise<RecoverResult> => {
      // 녹음 종료 때 띄운 업로드 실패 안내가 남아 있으면 먼저 닫는다 — 여기서 올리는 중에
      // 그 안내의 「취소」로 백업이 지워지거나 「다시 시도」로 겹쳐 올라가지 않게
      dialog.hide(uploadFailedDialogId(record.id));
      const invalidReason = getInvalidRecordingReason(
        record.recordFile,
        parseHHMMSS(record.duration),
      );
      if (invalidReason) {
        captureRecordError(
          'upload',
          new Error(`invalid queued recording: ${invalidReason}`),
          {
            docId: record.docId,
            title: record.title,
            mimeType: record.recordFile.type,
            blobSize: record.recordFile.size,
            duration: record.duration,
            reason: 'queued-invalid',
          },
        );
        await deleteFromQueue(record.id);
        return 'discarded';
      }
      let stage: 'upload' | 'attach' = 'upload';
      try {
        const uploadData = await uploadWithRetry(() =>
          useUploadFile(record.recordFile, true),
        );
        const recording: Recording = {
          name: uploadData.data.fileName,
          uuid: uploadData.data.uuid,
          duration: record.duration,
          state: 'complete',
        };
        stage = 'attach';
        await useSetAudioFile(record.docId, {
          uuid: recording.uuid,
          name: recording.name,
          duration: recording.duration,
        });
        // 그사이 다른 문서로 옮겨 갔으면 폼은 그 문서 것이다 — 서버 연결만 하고 폼은 건드리지 않는다
        if (isShowingDoc(record.docId)) {
          const current = getValues('recordings');
          if (!current.some((r) => r.uuid === recording.uuid)) {
            setValue('recordings', [...current, recording]);
          }
        }
        await deleteFromQueue(record.id);
        return 'recovered';
      } catch (e) {
        captureRecordError(stage, e, {
          docId: record.docId,
          title: record.title,
          mimeType: record.recordFile.type,
          blobSize: record.recordFile.size,
          duration: record.duration,
        });
        return 'failed';
      }
    },
    [dialog, getValues, setValue, isShowingDoc],
  );

  /**
   * 백업 1건 복구 — 'failed' 면 백업은 그대로 남는다.
   * 다른 탭이 맡았거나(잠금을 못 잡음) 이미 올려 지웠으면 'skipped' — 이 탭이 올린 게 아니다 (PORTAL-12586)
   */
  const recoverRecord = useCallback(
    async (record: QueuedRecord): Promise<RecoverResult> => {
      const result = await runWithLock(
        queueRecordLock(record.id),
        async (): Promise<RecoverResult> => {
          // 잠금을 기다리는 사이 다른 탭이 올리고 지웠을 수 있다
          if (!(await getQueuedRecordById(record.id))) return 'skipped';
          return uploadAndAttach(record);
        },
      );
      return result.ran ? result.value : 'skipped';
    },
    [uploadAndAttach],
  );

  /** 백업 여러 건을 차례로 복구 — 이 탭이 올린 건수와 실패한 건수를 돌려준다 */
  const recoverRecords = useCallback(
    async (records: QueuedRecord[]) => {
      const summary = { recovered: 0, failed: 0 };
      setLoading(true);
      try {
        for (const record of records) {
          const result = await recoverRecord(record);
          if (result === 'recovered') summary.recovered += 1;
          if (result === 'failed') summary.failed += 1;
        }
      } finally {
        setLoading(false);
      }
      return summary;
    },
    [recoverRecord, setLoading],
  );

  // ── 문서 화면의 실패 안내 (PORTAL-12583) ─────────────────────────
  // 진입할 때마다 띄우던 복구 안내창 대신, 음성 파일 칸에 실패 안내를 둔다(기획 요청 · Figma).
  // ⟳ 다시 올리기 · ✕ 이번 방문 동안 숨기기. 숨겨도 백업은 남아 다음 진입 때 다시 보인다 —
  // 유일한 원본이라 버리지 않는다.
  const [records, setRecords] = useState<QueuedRecord[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());
  const [retrying, setRetrying] = useState(false);

  const refresh = useCallback(async () => {
    const id = docIdRef.current;
    try {
      const list = await getQueuedRecords(id);
      // 읽는 사이 문서가 바뀌었으면 이전 문서 목록으로 새 문서 안내를 덮지 않는다
      if (docIdRef.current === id) setRecords(list);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const reportRecovered = useCallback(
    (recovered: number) => {
      if (recovered <= 0) return;
      showSuccess({
        message: t('업로드하지 못한 녹음 {{n}}건을 다시 올렸어요.', {
          n: recovered,
        }),
      });
    },
    [showSuccess],
  );

  const retry = useCallback(async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      const current = await getQueuedRecords(docIdRef.current);
      const { recovered } = await recoverRecords(current);
      reportRecovered(recovered);
    } finally {
      setRetrying(false);
      refresh();
    }
  }, [recoverRecords, refresh, reportRecovered, retrying]);

  // 숨긴 뒤 새로 실패한 녹음이 생기면 다시 보인다
  const dismiss = useCallback(
    () => setDismissedIds(new Set(records.map((r) => r.id))),
    [records],
  );
  const visible = records.filter((r) => !dismissedIds.has(r.id));

  // 안내에 넘기는 함수는 ref 로 고정한다 — 렌더마다 새 함수를 넘기면 안내를 읽는 쪽이
  // 다시 렌더될 때마다 안내가 또 바뀌어 렌더가 끝없이 돈다. 안내는 데이터가 바뀔 때만 갱신한다.
  const retryRef = useRef(retry);
  retryRef.current = retry;
  const dismissRef = useRef(dismiss);
  dismissRef.current = dismiss;
  const stableRetry = useCallback(() => retryRef.current(), []);
  const stableDismiss = useCallback(() => dismissRef.current(), []);

  const days =
    visible.length > 0 ? Math.min(...visible.map(getDaysUntilDeletion)) : 0;
  const setNotice = useSetAtom(queuedRecordNoticeAtom);
  useEffect(() => {
    setNotice(
      visible.length > 0
        ? {
            docId,
            count: visible.length,
            days,
            retrying,
            retry: stableRetry,
            dismiss: stableDismiss,
          }
        : null,
    );
  }, [
    visible.length,
    days,
    docId,
    retrying,
    setNotice,
    stableRetry,
    stableDismiss,
  ]);
  useEffect(() => () => setNotice(null), [setNotice]);

  // ── 온라인 복구 시 자동 재업로드 (PORTAL-12584) ────────────────────
  // 예전엔 배너만 '복구됨'으로 바뀌고 실제 재업로드는 문서에 다시 들어가야 일어났다.
  // 연결이 돌아오면 이 문서의 미복구 녹음을 묻지 않고 다시 올린다. 여기(문서 화면)서 하는
  // 이유: 업로드 뒤 문서 연결·폼 반영까지 해야 하는데, 문서 밖에선 폼이 없어 연결이 빠질 수 있다.
  useEffect(() => {
    const handleOnline = async () => {
      if ((await getQueuedRecords(docIdRef.current)).length === 0) return;
      await retryRef.current();
    };
    const onOnline = () => {
      handleOnline().catch(console.error);
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
    // 문서가 바뀔 때만 다시 건다 — 나머지는 ref 로 최신 값을 읽는다
  }, [docId]);

  // 큐가 바뀌면(실패 · 복구 · 비정상 종료 녹음 되살림 — PORTAL-12587) 다시 읽는다
  useEffect(() => {
    const handleQueueChanged = () => {
      refresh();
    };
    window.addEventListener(AUDIO_QUEUE_CHANGED_EVENT, handleQueueChanged);
    return () =>
      window.removeEventListener(AUDIO_QUEUE_CHANGED_EVENT, handleQueueChanged);
  }, [refresh]);

  useEffect(() => {
    setDismissedIds(new Set());
    refresh();
  }, [docId]);

  return { retry, recoverRecords };
};

export default useQueuedRecordRecovery;
